// 🔧 Configuration.tsx - VERSIÓN COMPLETA CON TODAS LAS SECCIONES
// ✅ Perfil, Suscripción, Cuenta, Privacidad, Notificaciones, Sistema

import React, { useState, useEffect } from 'react';
import { 
  User, Shield, Settings, Bell, Globe, Lock, Building, Mail, Phone, MapPin,
  CheckCircle, Save, RefreshCw, AlertTriangle, Crown, CreditCard, Calendar,
  Zap, Clock, Star, Award, Eye, EyeOff, Download, Upload, Trash2, Edit,
  Key, Database, Monitor, Wifi, HardDrive, Cpu, BarChart3, Users, FileText,
  Palette, Moon, Sun, Volume2, VolumeX, Smartphone, Laptop, Tablet,
  X, Check, Sparkles, Headphones, MessageCircle, Target, TrendingUp, DollarSign, Eye, EyeOff 
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useI18n } from '../hooks/useI18n';
import BillingSection from './Configuration/BillingSection';
import ServicePricingSettings from './Configuration/ServicePricingSettings';
import { useAuthStore } from '../stores/authStore';
import { ipService } from '../services/ipService';
// ===============================================
// 🔧 INTERFACES COMPLETAS
// ===============================================

interface User {
  id: string;
  name: string;
  email: string;
  username?: string;                    // ✅ AGREGAR - Nickname
  subscription_plan?: string;
  subscription_status?: string;
  organization_id?: string;
  role?: 'admin' | 'user';
  company?: string;
  phone?: string;
  country?: string;
  job_function?: string;                // ✅ AGREGAR - Función laboral
  personal_preferences?: string;        // ✅ AGREGAR - Preferencias personales
  preferences?: {
    language: string;
    theme: string;
    notifications: boolean;
  };
}

interface UserLimits {
  subscription: {
    current_plan: string;
    status: string;
    trial_ends_at?: string;
    trial_days_remaining?: number;
    subscription_expires_at?: string;
  };
  limits: {
    daily_queries?: { limit: number | null; is_unlimited: boolean; has_access: boolean; };
    whatsapp_support?: { has_access: boolean; };
    specialized_docs?: { has_access: boolean; };
    priority_support?: { has_access: boolean; };
  };
  usage: {
    daily_queries_used: number;
    daily_queries_remaining: number | null;
    daily_queries_limit: number | null;
    can_make_queries: boolean;
    needs_upgrade: boolean;
  };
}

interface SubscriptionPlan {
  plan_code: string;
  plan_name: string;
  description: string;
  pricing: {
    monthly: number;
    annual: number;
    annual_savings: number; 
    savings_percentage: number;
  };
  limits: {
    daily_queries: number | string;
    has_whatsapp_support: boolean;
    has_priority_support: boolean;
    has_specialized_docs: boolean;
  };
  badges: Array<{ text: string; color: string; icon: string; }>;
  highlights: string[];
  is_popular: boolean;
}

interface ConfigurationProps {
  currentUser: User | null;
  onUserUpdate: (user: User) => void;
}

// ===============================================
// 🎨 COMPONENTE PRINCIPAL
// ===============================================

const Configuration: React.FC = () => {
  const { user: currentUser, updateUser } = useAuthStore(); 
  const { isDark, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const { t } = useI18n();
  const { user } = useAuthStore();
  // ✅ Estados para suscripciones
  const [userLimits, setUserLimits] = useState<UserLimits | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'annual'>('monthly');

  // ✅ AGREGAR ESTOS ESTADOS NUEVOS al nivel principal
  const [userSubscription, setUserSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [creatingSubscription, setCreatingSubscription] = useState(false);
  const [activeSection, setActiveSection] = useState('perfil');
  
  console.log('👤 [CONFIG] Usuario actual:', user); // ✅ AGREGAR ESTA LÍNEA PARA DEBUG
  console.log('👤 [CONFIG] Rol del usuario:', user?.role); // ✅ AGREGAR ESTA LÍNEA
  
  // ✅ Estados del perfil
const [profileForm, setProfileForm] = useState({
  username: '', // ← AGREGADO
  name: '',
  email: '',
  displayName: '',
  jobFunction: '',
  company: '',
  phone: '',
  country: '',
  organizationId: '',
  personalPreferences: '',
  preferences: ''
});

// ✅ Estados de preferencias y sistema
  const [preferences, setPreferences] = useState({
    language: 'es',
    theme: isDark ? 'dark' : 'light',
    notifications: true,
    emailNotifications: true,
    systemUpdates: true,
    soundNotifications: true,
    browserNotifications: true,
    mobileNotifications: true,
    notificationSchedule: null,
    notificationSounds: null
  });

  // ✅ Estados de seguridad y privacidad
  const [securitySettings, setSecuritySettings] = useState({
    showEmail: false,
    twoFactorEnabled: false,
    sessionTimeout: '30',
    autoLogout: true,
    rememberMe: true,
    passwordExpiry: '90',
    loginAlerts: true
  });

  // ✅ Estados de cuenta y organización
  const [accountSettings, setAccountSettings] = useState({
    timezone: 'America/Santiago',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    currency: 'USD',
    autoSave: true,
    dataRetention: '1year',
    backupEnabled: true
  });

  // ✅ Estados de sistema
  const [systemInfo, setSystemInfo] = useState({
    serverStatus: 'online',
    lastBackup: '',
    storageUsed: 0,
    apiCalls: 0,
    uptime: '',
    version: '2.1.0'
  });
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSoundsModal, setShowSoundsModal] = useState(false);
  // ===============================================
  // 🔄 EFECTOS Y FUNCIONES DE CARGA
  // ===============================================

useEffect(() => {
  // ✅ VALIDACIÓN ROBUSTA: Verificar currentUser y su ID antes de cargar
  if (!currentUser) {
    console.log('⚠️ [CONFIG] No hay currentUser disponible');
    return;
  }
  
  if (!currentUser.id) {
    console.error('❌ [CONFIG] currentUser existe pero no tiene id:', currentUser);
    return;
  }
  
  console.log('📄 [CONFIG] Iniciando carga de datos para usuario:', currentUser.id);
  loadUserProfile();
  loadUserLimits();
  loadAvailablePlans();
  loadSystemInfo();
}, [currentUser]);

  useEffect(() => {
   if (currentUser) {
     setProfileForm({
       username: currentUser.username || '', // ✅ currentUser sí existe
       name: currentUser.name || '',
       email: currentUser.email || '',
       displayName: currentUser.name || '',
       jobFunction: currentUser.job_function || '',
       company: currentUser.company || '',
       phone: currentUser.phone || '',
       country: currentUser.country || '',
       organizationId: currentUser.organization_id || '',
       preferences: currentUser.preferences || '',
       personalPreferences: currentUser.personal_preferences || ''
     });
   }
 }, [currentUser]);

 useEffect(() => {
  const loadUserSubscription = async () => {
    try {
      const token = localStorage.getItem('kikai_token') || 
                   localStorage.getItem('token') ||
                   sessionStorage.getItem('token');

      if (!token) {
        console.warn('⚠️ No hay token de autenticación');
        setLoadingSubscription(false);
        return;
      }

      const response = await fetch('/api/user/subscription', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // ✅ DEBUGGING COMPLETO
        console.log('🔍 [API-DEBUG] Respuesta completa del API:', data);
        console.log('🔍 [API-DEBUG] data.success:', data.success);
        console.log('🔍 [API-DEBUG] data.subscription:', data.subscription);
        console.log('🔍 [API-DEBUG] Keys disponibles:', Object.keys(data));
        
        if (data.success) {
          // ✅ MAPEO ROBUSTO con múltiples fuentes
          const subscriptionData = data.subscription || data.user_subscription || data.current_subscription || {};
          
          console.log('🔍 [API-DEBUG] subscriptionData mapeado:', subscriptionData);
          
          // ✅ MAPEO MANUAL si viene en formato diferente
          const mappedSubscription = {
            plan: subscriptionData.plan || subscriptionData.plan_code || subscriptionData.subscription_type || 'standard',
            plan_code: subscriptionData.plan_code || subscriptionData.plan || subscriptionData.subscription_type || 'standard',
            status: subscriptionData.status || subscriptionData.subscription_status || 'inactive',
            expires_at: subscriptionData.expires_at || subscriptionData.subscription_expires_at,
            // Preservar otros campos
            ...subscriptionData
          };
          
          console.log('🔍 [API-DEBUG] Subscription final mapeada:', mappedSubscription);
          
          setUserSubscription(mappedSubscription);
          setAvailablePlans(data.available_plans || []);
        }
      } else {
        console.error('❌ Error response:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error cargando suscripción:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  loadUserSubscription();
}, [currentUser?.id]); // ✅ Dependencia específica
 
  // ✅ FUNCIONES DE CARGA (mantener las existentes)
  const loadUserProfile = async () => {
  if (!currentUser?.id) {
    console.log('⚠️ [PROFILE] No hay usuario actual o ID:', { 
      hasUser: !!currentUser, 
      userId: currentUser?.id 
    });
    return;
  }

  try {
    console.log('📊 [PROFILE] Cargando perfil para usuario:', currentUser.id);
    setLoading(true);
    setError('');

    // ✅ USAR EL NOMBRE CORRECTO DEL TOKEN
    const token = localStorage.getItem('kikai_token'); // ← CAMBIADO de 'authToken'
    
    if (!token) {
      console.error('❌ [PROFILE] Token no encontrado en localStorage');
      setError('Token de autenticación no encontrado');
      return;
    }

    console.log('🔑 [PROFILE] Token encontrado:', token.substring(0, 20) + '...');

    const response = await fetch('/api/user/profile', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('📡 [PROFILE] Respuesta del servidor:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [PROFILE] Error HTTP:', response.status, errorText);
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('📦 [PROFILE] Datos recibidos:', data);
    
    if (data.success && data.user) {
      const userData = data.user;
      
      // ✅ MAPEAR DATOS CORRECTAMENTE
      setProfileForm({
          username: userData.username || '', // ✅ Usar userData
          name: userData.name || '',
          email: userData.email || '',
          displayName: userData.name || '',
          jobFunction: userData.job_function || '',
          company: userData.company || '',
          phone: userData.phone || '',
          country: userData.country || '',
          organizationId: userData.organization_id || '',
          preferences: userData.preferences || '',
          personalPreferences: userData.personal_preferences || ''
        });

      
      if (data.user.account_settings) {
        setAccountSettings(data.user.account_settings);
      }
      
      if (data.user.security_settings) {
        setSecuritySettings(data.user.security_settings);
      }
      
      if (data.user.preferences) {
        setPreferences(data.user.preferences);
      }

      if (userData.preferences) {
        setPreferences(prev => ({
          ...prev,
          ...userData.preferences
        }));
      }

      console.log('✅ [PROFILE] Perfil de usuario cargado exitosamente');
    } else {
      console.error('❌ [PROFILE] Respuesta inválida:', data);
      setError('Formato de respuesta inválido del servidor');
    }

  } catch (error) {
    console.error('❌ [PROFILE] Error cargando perfil:', error);
    setError(`Error al cargar la información del usuario: ${error.message}`);
  } finally {
    setLoading(false);
  }
};  

  const loadAvailablePlans = async () => {
    try {
      const response = await fetch('/api/subscription-plans');
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailablePlans(data.plans);
          console.log('✅ Planes de suscripción cargados:', data.plans);
        }
      }
    } catch (error) {
      console.error('❌ Error cargando planes:', error);
    }
  };  
  
  const loadSystemInfo = async () => {
    try {
      const response = await fetch('/api/system/info');
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSystemInfo(data.system);
        }
      }
    } catch (error) {
      console.error('❌ Error cargando info del sistema:', error);
      // Datos de ejemplo si falla
      setSystemInfo({
        serverStatus: 'online',
        lastBackup: new Date().toISOString(),
        storageUsed: 2.5,
        apiCalls: 1250,
        uptime: '15 días, 6 horas',
        version: '2.1.0'
      });
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('💾 [SAVE] Guardando configuración...');
      
      // Simular guardado
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setHasChanges(false);
      setShowSuccess(true);
      console.log('✅ [SAVE] Configuración guardada exitosamente');
      
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('❌ [SAVE] Error guardando:', error);
      setError('Error guardando la configuración');
    } finally {
      setSaving(false);
    }
  };
  
  // ✅ FUNCIÓN CORREGIDA: loadUserLimits
  const loadUserLimits = async () => {
  try {
    // ✅ USAR EL NOMBRE CORRECTO DEL TOKEN
    const token = localStorage.getItem('kikai_token'); // ← CAMBIADO de 'authToken'
    
    if (!token) {
      console.warn('⚠️ [LIMITS] Token no encontrado para cargar límites');
      return;
    }

    const response = await fetch('/api/user/limits', {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        setUserLimits(data);
        console.log('✅ [LIMITS] Límites de usuario cargados:', data);
      }
    } else {
      console.warn('⚠️ [LIMITS] Error cargando límites:', response.status);
    }
  } catch (error) {
    console.error('❌ [LIMITS] Error cargando límites:', error);
  }
};
// ✅ FUNCIÓN CORREGIDA: saveChanges
  const saveChanges = async () => {
  if (!currentUser?.id || !hasChanges) return;

  try {
    setSaving(true);
    setError('');

    // ✅ USAR EL NOMBRE CORRECTO DEL TOKEN
    const token = localStorage.getItem('kikai_token'); // ← CAMBIADO de 'authToken'
    
    if (!token) {
      setError('Token de autenticación no encontrado');
      return;
    }
    
    // ✅ OBTENER IP PÚBLICA
    const publicIp = await ipService.getPublicIP();
    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      publicIp: publicIp
    };

    const response = await fetch('/api/user/profile', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username: profileForm.username, // ← AGREGADO
        name: profileForm.name,
        company: profileForm.company,
        phone: profileForm.phone,
        country: profileForm.country,
        job_function: profileForm.jobFunction,
        personal_preferences: profileForm.personalPreferences,
        preferences: profileForm.preferences,
        security_settings: securitySettings,
        account_settings: accountSettings,
        deviceInfo  // ✅ AGREGAR
      })
    });

    if (!response.ok) {
      throw new Error('Error al actualizar perfil');
    }

    const data = await response.json();

   if (data.success) {
  // ✅ Actualizar con TODOS los campos del formulario
    const updatedUser = {
      ...currentUser,
      username: profileForm.username,              // ✅ AGREGAR
      name: profileForm.name,
      company: profileForm.company,
      phone: profileForm.phone,
      country: profileForm.country,
      preferences: profileForm.preferences,// Ya se envía en el body
      job_function: profileForm.jobFunction,       // ✅ AGREGAR
      personal_preferences: profileForm.personalPreferences  // ✅ AGREGAR
    };
         
      updateUser(updatedUser);
      setHasChanges(false);
      setShowSuccess(true);
      
      setTimeout(() => setShowSuccess(false), 3000);
      
      console.log('✅ [SAVE] Perfil actualizado exitosamente');
    }

  } catch (error) {
    console.error('❌ [SAVE] Error guardando perfil:', error);
    setError('Error al guardar los cambios');
  } finally {
    setSaving(false);
  }
};
  // ✅ MANEJAR CAMBIOS EN FORMULARIOS
  const handleFormChange = (field: string, value: string) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };
  
  const handleDisableAllNotifications = () => {
  if (confirm('¿Estás seguro de que deseas deshabilitar todas las notificaciones? Esto afectará todas las alertas excepto las críticas de seguridad.')) {
    setPreferences(prev => ({
      ...prev,
      notifications: false,
      emailNotifications: false,
      systemUpdates: false,
      soundNotifications: false,
      browserNotifications: false,
      mobileNotifications: false
    }));
    setHasChanges(true);
    alert('✅ Todas las notificaciones han sido deshabilitadas');
  }
};

  const handleDownloadRecoveryKeys = () => {
  try {
    const recoveryKeys = {
      userId: currentUser?.id,
      email: currentUser?.email,
      generatedAt: new Date().toISOString(),
      keys: [
        generateRecoveryKey(),
        generateRecoveryKey(),
        generateRecoveryKey(),
        generateRecoveryKey(),
        generateRecoveryKey()
      ],
      instructions: [
        '1. Guarda estas claves en un lugar seguro',
        '2. Nunca las compartas con nadie',
        '3. Úsalas solo si pierdes acceso a tu cuenta',
        '4. Cada clave solo puede usarse una vez'
      ]
    };

    const dataStr = JSON.stringify(recoveryKeys, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `kikai-recovery-keys-${currentUser?.id}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    alert('✅ Claves de recuperación descargadas exitosamente');
  } catch (error) {
    console.error('❌ Error descargando claves:', error);
    alert('Error al generar las claves de recuperación');
  }
};

const generateRecoveryKey = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let key = '';
  for (let i = 0; i < 20; i++) {
    if (i > 0 && i % 4 === 0) key += '-';
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
};

  const handlePreferenceChange = (field: string, value: any) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSecurityChange = (field: string, value: any) => {
    setSecuritySettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleAccountChange = (field: string, value: any) => {
    setAccountSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };
  
  const handleSelectPlan = async (planId, billingType) => {
    try {
      setCreatingSubscription(true);
      
      const token = localStorage.getItem('kikai_token') || 
                   localStorage.getItem('token') ||
                   sessionStorage.getItem('token');

      if (!token) {
        alert('Sesión expirada. Por favor, inicia sesión nuevamente.');
        return;
      }

      console.log('🔄 Creando suscripción:', { planId, billingType });

      const response = await fetch('/api/subscriptions/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_type: billingType,  // CORREGIDO: backend espera plan_type
          plan_code: planId
        })
      });

      if (!response.ok) {
        let errorMessage = `Error HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          console.warn('No se pudo parsear error del servidor');
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (result.success) {
        setUserSubscription({
          ...userSubscription,
          plan_code: planId,
          status: 'active',
          expires_at: result.expires_at || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        });
        
        setShowPlansModal(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        console.log('✅ Suscripción creada exitosamente:', result);
      } else {
        throw new Error(result.error || 'Error desconocido');
      }

    } catch (error) {
      console.error('❌ Error creando suscripción:', error);
      setError(error.message || 'Error al crear la suscripción');
      setTimeout(() => setError(''), 5000);
    } finally {
      setCreatingSubscription(false);
    }
  };
  const allMenuItems = [
    { id: 'perfil', label:  t('configuration.menu.perfil'), icon: User },
    { id: 'suscripcion', label: t('configuration.menu.subscription'), icon: Crown },
    { id: 'facturacion', label: 'Facturación', icon: CreditCard }, // ← NUEVA LÍNEA
    { id: 'precios-servicio', label: 'Precios de Servicio', icon: DollarSign, adminOnly: true }, // ✅ MARCAR COMO ADMIN
    { id: 'cuenta', label:  t('configuration.menu.account'), icon: Settings },
    { id: 'privacidad', label: t('configuration.menu.privacy'), icon: Shield },
    { id: 'notificaciones', label:  t('configuration.menu.notifications'), icon: Bell },
    { id: 'sistema', label: t('configuration.menu.system'), icon: Globe }
  ];
  
  // ✅ Agregar item de precios solo si es admin
const menuItems = allMenuItems.filter(item => 
  !item.adminOnly || user?.role === 'admin'
);

 /**
 * 💳 SECCIÓN DE FACTURACIÓN
 */
const renderFacturacionSection = () => {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-700 pb-4 mb-6">
        <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          💳 Facturación
        </h3>
        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Gestiona tu método de pago, plan actual e historial de facturas
        </p>
      </div>
      
      {/* Componente de Facturación */}
      <BillingSection />
    </div>
  );
};
const renderPerfilSection = () => (

    <div className="space-y-6">
      <div>
        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
           {t('configuration.perfil.title')}
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
           {t('configuration.perfil.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nombre completo */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('configuration.perfil.name')}
          </label>  
          <div className="relative">
            <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              isDark ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {profileForm.name.charAt(0).toUpperCase() || 'U'}
            </div>
            <input
              type="text"
              value={profileForm.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              className={`w-full pl-14 pr-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
              placeholder={t('configuration.perfil.nameplaceholder')}
            />
          </div>
        </div>

        {/* Nombre de visualización */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('configuration.perfil.nickname')}
          </label>
          <input
            type="text"
            value={profileForm.username}
            onChange={(e) => handleFormChange('username', e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border transition-all ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:ring-2 focus:ring-blue-200`}
            placeholder={t('configuration.perfil.nicknameplaceholder')}
          />
        </div>

        {/* Email */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('configuration.perfil.email')}
          </label>
          <div className="relative">
            <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="email"
              value={profileForm.email}
              disabled
              className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed' 
                  : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              placeholder={t('configuration.perfil.emailplaceholder')}
            />
          </div>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('configuration.perfil.emailinfo')}
          </p>
        </div>

        {/* Función laboral */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('configuration.perfil.job')}
          </label>
          <select
            value={profileForm.jobFunction}
            onChange={(e) => handleFormChange('jobFunction', e.target.value)}
            className={`w-full px-4 py-3 rounded-lg border transition-all ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:ring-2 focus:ring-blue-200`}
          >
            <option value="">{t('configuration.perfil.select1')}</option>
            <option value="admin">{t('configuration.perfil.select2')}</option>
            <option value="mecanico">{t('configuration.perfil.select3')}</option>
            <option value="supervisor">{t('configuration.perfil.select4')}</option>
            <option value="ingeniero">{t('configuration.perfil.select5')}</option>
            <option value="gerente">{t('configuration.perfil.select6')}</option>
            <option value="operador">{t('configuration.perfil.select7')}</option>
            <option value="estudiante">{t('configuration.perfil.select8')}</option>
            <option value="otro">{t('configuration.perfil.select9')}</option>
          </select>
        </div>

        {/* Empresa */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
           {t('configuration.perfil.company')}
          </label>
          <div className="relative">
            <Building className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              value={profileForm.company}
              onChange={(e) => handleFormChange('company', e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
              placeholder={t('configuration.perfil.companyplaceholder')}
            />
          </div>
        </div>

        {/* Teléfono */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
             {t('configuration.perfil.phone')}
          </label>
          <div className="relative">
            <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="tel"
              value={profileForm.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
              placeholder= {t('configuration.perfil.phoneplaceholder')} 
            />
          </div>
        </div>
      </div>

      {/* Preferencias del sistema */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
        <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('configuration.perfil.preferences')}
        </h4>
        <div className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
           {t('configuration.perfil.preferencessubtitle')}
        </div>
        <textarea
          value={profileForm.preferences} 
          onChange={(e) => handleFormChange('preferences', e.target.value)}
          className={`w-full px-4 py-3 rounded-lg border transition-all resize-none ${
            isDark 
              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
              : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
          } focus:ring-2 focus:ring-blue-200`}
          rows={3}
          placeholder= {t('configuration.perfil.preferencesplaceholder')}
        />
      </div>
    </div>
  );

  // ===============================================
  // 💎 SECCIÓN SUSCRIPCIÓN (mantener la existente)
  // ===============================================

  // Definición de los 3 planes
const SUBSCRIPTION_PLANS = [
  {
    id: 'standard',
    name: 'Standard',
    subtitle: 'Ideal para técnicos con consultas básicas',
    icon: Zap,
    color: 'blue',
    priceMonthly: 50,
    priceAnnual: 560,
    savings: 40,
    features: [
      { text: '50 consultas diarias', included: true, icon: Target },
      { text: 'Soporte WhatsApp especializado', included: false, icon: MessageCircle },
      { text: 'Documentación especializada', included: false, icon: FileText },
      { text: 'Soporte prioritario', included: false, icon: Headphones }
    ],
    badge: null,
    buttonText: 'Obtener plan Standard'
  },
  {
    id: 'pro',
    name: 'Pro',
    subtitle: 'Investiga, programa y organiza',
    icon: Star,
    color: 'purple',
    priceMonthly: 17,
    priceAnnual: 170,
    savings: 34,
    isAnnualOnly: true,
    features: [
      { text: 'Consultas ilimitadas', included: true, icon: Target },
      { text: 'Soporte WhatsApp especializado', included: true, icon: MessageCircle },
      { text: 'Documentación especializada', included: true, icon: FileText },
      { text: 'Soporte prioritario', included: true, icon: Headphones },
      { text: 'Proyectos técnicos avanzados', included: true, icon: TrendingUp }
    ],
    badge: { text: 'Más Popular', color: 'green' },
    buttonText: 'Obtener plan Pro anual'
  },
  {
    id: 'max',
    name: 'Max',
    subtitle: 'Límites más altos, acceso prioritario',
    icon: Award,
    color: 'gold',
    priceMonthly: 100,
    priceAnnual: 1100,
    savings: 100,
    features: [
      { text: 'Todo de Pro, más:', included: true, icon: Check },
      { text: 'Límites de generación más altos', included: true, icon: TrendingUp },
      { text: 'Acceso anticipado a funciones avanzadas', included: true, icon: Sparkles },
      { text: 'Acceso prioritario durante períodos de alto tráfico', included: true, icon: Shield }
    ],
    badge: null,
    buttonText: 'Obtener plan Max'
  }
];

// Modal de selección de planes
const PlansModal = ({ isOpen, onClose, onSelectPlan, currentPlan, billingCycle, setBillingCycle }) => {
  const { isDark } = useTheme();
  const { t } = useI18n();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-6xl w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        <div className={`sticky top-0 p-6 border-b ${
          isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className={`text-2xl font-bold ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Elige tu plan
              </h2>
              <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Actualiza tu suscripción para obtener más beneficios
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
              }`}
            >
              <X className={`h-6 w-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>

          {/* Toggle Mensual/Anual */}
          <div className="flex items-center justify-center mt-6">
            <div className={`flex items-center rounded-lg p-1 ${
              isDark ? 'bg-gray-800' : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingCycle === 'annual'
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Anual
                <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                  Ahorra hasta 34%
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {SUBSCRIPTION_PLANS.map((plan) => {
              const IconComponent = plan.icon;
              const isCurrentPlan = currentPlan === plan.id;
              const price = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly;
              const monthlyPrice = billingCycle === 'annual' ? plan.priceAnnual / 12 : plan.priceMonthly;
              
              return (
                <div
                  key={plan.id}
                  className={`relative p-6 rounded-xl border-2 transition-all ${
                    plan.badge
                      ? isDark ? 'border-green-500 bg-gray-800' : 'border-green-500 bg-green-50'
                      : isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                  } ${isCurrentPlan ? 'ring-2 ring-blue-500' : ''}`}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        plan.badge.color === 'green' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}>
                        {plan.badge.text}
                      </span>
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrentPlan && (
                    <div className="absolute -top-3 right-4">
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        Plan Actual
                      </span>
                    </div>
                  )}

                  {/* Header */}
                  <div className="text-center mb-6">
                    <div className={`inline-flex p-3 rounded-full mb-4 ${
                      plan.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                      plan.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                      'bg-yellow-100 text-yellow-600'
                    }`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                    
                    <h3 className={`text-xl font-bold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      {plan.name}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {plan.subtitle}
                    </p>
                  </div>

                  {/* Pricing */}
                  <div className="text-center mb-6">
                    <div className={`text-3xl font-bold ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>
                      USD {Math.round(monthlyPrice)}
                      <span className={`text-sm font-normal ${
                        isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        /mes {billingCycle === 'annual' ? 'facturado anualmente' : 'facturado mensualmente'}
                      </span>
                    </div>
                    
                    {billingCycle === 'annual' && plan.savings > 0 && (
                      <p className="text-sm text-green-600 mt-1">
                        Ahorra ${plan.savings}/año vs mensual
                      </p>
                    )}
                    
                    {plan.isAnnualOnly && billingCycle === 'monthly' && (
                      <p className="text-xs text-orange-600 mt-1">
                        Este plan solo está disponible con facturación anual
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div key={index} className="flex items-start">
                          {feature.included ? (
                            <Check className="h-4 w-4 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                          ) : (
                            <X className="h-4 w-4 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
                          )}
                          <div className="flex items-center">
                            <FeatureIcon className={`h-4 w-4 mr-2 ${
                              feature.included ? 'text-green-500' : 'text-gray-400'
                            }`} />
                            <span className={`text-sm ${
                              feature.included 
                                ? isDark ? 'text-white' : 'text-gray-900'
                                : isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {feature.text}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => onSelectPlan(plan.id, billingCycle)}
                    disabled={isCurrentPlan || (plan.isAnnualOnly && billingCycle === 'monthly')}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      isCurrentPlan
                        ? isDark ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : plan.isAnnualOnly && billingCycle === 'monthly'
                        ? isDark ? 'bg-gray-700 text-gray-300 cursor-not-allowed' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : plan.badge
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isCurrentPlan ? 'Plan Actual' : plan.buttonText}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente principal - Mi Suscripción
// 🔧 REEMPLAZAR SOLO renderSuscripcionSection en Configuration.tsx
// ✅ Función corregida con manejo de errores y hooks apropiados

const renderSuscripcionSection = () => {
  // ✅ USAR LOS CAMPOS CORRECTOS DEL API
  const currentPlanCode = userLimits?.subscription?.current_plan || 'standard';
  const planStatus = userLimits?.subscription?.status || 'inactive';
  
  // ✅ MAPEO DE CÓDIGOS A NOMBRES
  const planNames = {
    'premium': 'Premium',
    'profesional': 'Profesional',
    'professional': 'Profesional',
    'standard': 'Standard',
    'free': 'Gratis'
  };
  
  const displayName = planNames[currentPlanCode] || 'Standard';
  
  console.log('🔍 [SUBSCRIPTION-DEBUG] Datos de suscripción:', {
    currentPlanCode,
    planStatus,
    displayName,
    userLimits
  });
  
  // ✅ Determinar características del plan basado en el código
  const getPlanFeatures = (planCode) => {
    switch (planCode) {
      case 'premium':
        return {
          queries: 'Ilimitadas',
          whatsapp: '✅ Disponible',
          docs: '✅ Disponible', 
          support: 'Prioritario',
          color: 'yellow',
          icon: Crown
        };
      case 'profesional':
      case 'professional':
        return {
          queries: 'Ilimitadas',
          whatsapp: '❌ No disponible',
          docs: '✅ Disponible',
          support: 'Estándar',
          color: 'purple',
          icon: Star
        };
      case 'standard':
      default:
        return {
          queries: '50/día',
          whatsapp: '❌ No disponible',
          docs: '❌ No disponible',
          support: 'Email básico',
          color: 'blue',
          icon: User
        };
    }
  };

  const planFeatures = getPlanFeatures(currentPlanCode);
  const isActive = planStatus === 'active';
  const isExpired = planStatus === 'expired';
  const isTrial = planStatus === 'trial';

  const mapApiPlansToModalFormat = (apiPlans) => {
  return apiPlans.map(plan => {
    // ✅ CREAR NOMBRE basado en plan_code si plan_name es undefined
    const getPlanName = (planData) => {
      // Múltiples fuentes posibles para el nombre
      if (planData.plan_name && planData.plan_name !== 'undefined') {
        return planData.plan_name;
      }
      if (planData.name) {
        return planData.name;
      }
      // ✅ FALLBACK basado en plan_code
      const nameMap = {
        'standard': 'Standard',
        'profesional': 'Profesional',
        'premium': 'Premium',
        'pro': 'Profesional',
        'max': 'Premium'
      };
      return nameMap[planData.plan_code] || 'Plan Básico';
    };

    return {
      id: plan.plan_code,
      name: getPlanName(plan), // ✅ USAR FUNCIÓN QUE GARANTIZA UN NOMBRE
      description: plan.description || plan.plan_description || `Plan ${getPlanName(plan)}`,
      price: {
        monthly: parseFloat(plan.price_monthly || plan.pricing?.monthly || 50),
        annual: parseFloat(plan.price_annual || plan.pricing?.annual || 560)
      },
      features: plan.features ? 
        (typeof plan.features === 'string' ? JSON.parse(plan.features) : 
         Array.isArray(plan.features) ? plan.features :
         Object.values(plan.features)) : 
        [`${plan.limits?.daily_queries || 'Consultas básicas'}`, 'Soporte incluido'],
      color: plan.plan_code === 'premium' ? 'gold' : 
             plan.plan_code === 'profesional' ? 'purple' : 'blue',
      popular: Boolean(plan.is_popular),
      icon: plan.plan_code === 'premium' ? Crown : 
            plan.plan_code === 'profesional' ? Star : User
    };
  });
};

  // ✅ USAR DATOS REALES o mostrar loading
  const plansToDisplay = availablePlans.length > 0 
    ? mapApiPlansToModalFormat(availablePlans) 
    : [];

  // ✅ DEBUG para ver los datos reales
  console.log('🔍 [PLANS-DEBUG] availablePlans from API:', availablePlans);
  console.log('🔍 [PLANS-DEBUG] mapped plans:', plansToDisplay);

  // ✅ Si no hay planes de la API, mostrar loading
  if (availablePlans.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin mr-3" />
        <span className={isDark ? 'text-white' : 'text-gray-900'}>
          Cargando planes disponibles...
        </span>
      </div>
    );
  }
  
  
  // Loading state
  if (loadingSubscription) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin mr-3" />
        <span className={isDark ? 'text-white' : 'text-gray-900'}>
          Cargando suscripción...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-700 pb-4 mb-6">
        <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          👑 Mi Suscripción
        </h3>
        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Gestiona tu plan y características de suscripción
        </p>
      </div>

      {/* Estado actual de la suscripción */}
      <div className={`p-6 rounded-xl border ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <planFeatures.icon className={`h-6 w-6 mr-3 ${
              planFeatures.color === 'blue' ? 'text-blue-500' :
              planFeatures.color === 'purple' ? 'text-purple-500' :
              'text-yellow-500'
            }`} />
            <div>
              <h4 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Plan {displayName}
              </h4>
              <p className={`text-sm ${
                isActive ? 'text-green-500' : 
                isExpired ? 'text-red-500' : 
                'text-yellow-500'
              }`}>
                {isActive ? '🟢 Activo' : 
                 isExpired ? '🔴 Expirado' : 
                 '🟡 Periodo de prueba'}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowPlansModal(true)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isExpired 
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isExpired ? 'Renovar Suscripción' : 'Cambiar Plan'}
            </button>
          </div>
        </div>

        {/* ✅ INFORMACIÓN DEL PLAN ACTUALIZADA */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <Zap className="h-4 w-4 mr-2 text-blue-500" />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Consultas
              </span>
            </div>
            <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {planFeatures.queries}
            </p>
          </div>

          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                WhatsApp
              </span>
            </div>
            <p className={`text-sm font-medium ${
              planFeatures.whatsapp.includes('✅') ? 'text-green-500' : 'text-red-500'
            }`}>
              {planFeatures.whatsapp}
            </p>
          </div>

          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <FileText className="h-4 w-4 mr-2 text-purple-500" />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Docs Especializada
              </span>
            </div>
            <p className={`text-sm font-medium ${
              planFeatures.docs.includes('✅') ? 'text-green-500' : 'text-red-500'
            }`}>
              {planFeatures.docs}
            </p>
          </div>

          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div className="flex items-center mb-2">
              <Headphones className="h-4 w-4 mr-2 text-orange-500" />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Soporte
              </span>
            </div>
            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {planFeatures.support}
            </p>
          </div>
        </div>

        {/* Información adicional */}
        {userSubscription?.expires_at && (
          <div className={`mt-4 p-3 rounded-lg border-l-4 ${
            isActive ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
            'border-red-500 bg-red-50 dark:bg-red-900/20'
          }`}>
            <p className={`text-sm ${
              isActive ? 'text-green-700 dark:text-green-300' :
              'text-red-700 dark:text-red-300'
            }`}>
              {isActive ? '✅ Suscripción válida hasta:' : '⚠️ Suscripción expiró el:'} {' '}
              {new Date(userSubscription.expires_at).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        )}
      </div>

      {/* Modal de planes */}
      {showPlansModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-xl ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Header del modal */}
            <div className="sticky top-0 p-6 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Selecciona tu Plan
                </h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Elige el plan que mejor se adapte a tus necesidades
                </p>
              </div>
              <button
                onClick={() => setShowPlansModal(false)}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Toggle de facturación */}
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-center">
                <div className={`p-1 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <button
                    onClick={() => setSelectedBilling('monthly')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedBilling === 'monthly'
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Mensual
                  </button>
                  <button
                    onClick={() => setSelectedBilling('annual')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedBilling === 'annual'
                        ? 'bg-blue-600 text-white'
                        : isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Anual (Ahorra hasta 20%)
                  </button>
                </div>
              </div>
            </div>

            {/* Planes */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plansToDisplay.map((plan) => {
                  const price = selectedBilling === 'monthly' ? plan.price.monthly : plan.price.annual;
                  const savings = selectedBilling === 'annual' ? (plan.price.monthly * 12) - plan.price.annual : 0;
                  const isCurrentPlan = currentPlanCode === plan.id;


                  return (
                    <div
                      key={plan.id}
                      className={`p-6 rounded-xl border relative ${
                        plan.popular 
                          ? 'border-blue-500 ring-2 ring-blue-200' 
                          : isDark ? 'border-gray-700' : 'border-gray-200'
                      } ${isDark ? 'bg-gray-800' : 'bg-white'}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                            Más Popular
                          </span>
                        </div>
                      )}

                      <div className="text-center">
                        <h4 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {plan.name}
                        </h4>
                        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {plan.description}
                        </p>

                        <div className="mt-4 mb-6">
                          <div className="flex items-baseline justify-center">
                            <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              ${price}
                            </span>
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              /{selectedBilling === 'monthly' ? 'mes' : 'año'}
                            </span>
                          </div>
                          {selectedBilling === 'annual' && savings > 0 && (
                            <p className="text-sm text-green-500 mt-1">
                              Ahorra ${savings} al año
                            </p>
                          )}
                        </div>

                        <ul className="space-y-3 mb-6 text-left">
                          {plan.features.map((feature, index) => (
                            <li key={index} className={`text-sm flex items-center ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>

                        <button
                          onClick={() => handleSelectPlan(plan.id, selectedBilling)}
                          disabled={isCurrentPlan || creatingSubscription}
                          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                            isCurrentPlan
                              ? isDark 
                                ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : creatingSubscription
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : plan.popular
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : isDark
                              ? 'bg-gray-700 hover:bg-gray-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                          }`}
                        >
                          {creatingSubscription ? (
                            <div className="flex items-center justify-center">
                              <RefreshCw className="animate-spin w-4 h-4 mr-2" />
                              Procesando...
                            </div>
                          ) : isCurrentPlan ? (
                            'Plan Actual'
                          ) : (
                            `Seleccionar ${plan.name}`
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
  // ===============================================
  // ⚙️ SECCIÓN CUENTA
  // ===============================================

  const renderCuentaSection = () => (
  
    <div className="space-y-6">
      <div>
        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Configuración de Cuenta
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Gestiona tu cuenta, organización y configuración del sistema
        </p>
      </div>

      {/* Información de la Organización */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Building className="w-5 h-5 mr-2" />
          Información de la Organización
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              ID de Organización
            </label>
            <div className="relative">
              <input
                type="text"
                value={profileForm.organizationId || 'ORG-' + String(currentUser?.id || '').slice(-8)}
                disabled
                className={`w-full px-4 py-3 rounded-lg border transition-all ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed' 
                    : 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                placeholder="ORG-12345678"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
            </div>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              ID único de tu organización generado automáticamente
            </p>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              País
            </label>
            <div className="relative">
              <MapPin className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`} />
              <select
                value={profileForm.country}
                onChange={(e) => handleFormChange('country', e.target.value)}
                className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-all ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                } focus:ring-2 focus:ring-blue-200`}
              >
                <option value="">Seleccionar país</option>
                <option value="CL">Chile</option>
                <option value="AR">Argentina</option>
                <option value="PE">Perú</option>
                <option value="CO">Colombia</option>
                <option value="MX">México</option>
                <option value="US">Estados Unidos</option>
                <option value="ES">España</option>
                <option value="OTHER">Otro</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Configuración Regional */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Globe className="w-5 h-5 mr-2" />
          Configuración Regional
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Zona horaria
            </label>
            <select
              value={accountSettings.timezone}
              onChange={(e) => handleAccountChange('timezone', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="America/Santiago">Santiago (GMT-3)</option>
              <option value="America/Buenos_Aires">Buenos Aires (GMT-3)</option>
              <option value="America/Lima">Lima (GMT-5)</option>
              <option value="America/Bogota">Bogotá (GMT-5)</option>
              <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
              <option value="America/New_York">Nueva York (GMT-5)</option>
              <option value="Europe/Madrid">Madrid (GMT+1)</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Formato de fecha
            </label>
            <select
              value={accountSettings.dateFormat}
              onChange={(e) => handleAccountChange('dateFormat', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="DD/MM/YYYY">DD/MM/AAAA (31/12/2024)</option>
              <option value="MM/DD/YYYY">MM/DD/AAAA (12/31/2024)</option>
              <option value="YYYY-MM-DD">AAAA-MM-DD (2024-12-31)</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Formato de hora
            </label>
            <select
              value={accountSettings.timeFormat}
              onChange={(e) => handleAccountChange('timeFormat', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="24h">24 horas (14:30)</option>
              <option value="12h">12 horas (2:30 PM)</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Moneda
            </label>
            <select
              value={accountSettings.currency}
              onChange={(e) => handleAccountChange('currency', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="USD">Dólar estadounidense (USD)</option>
              <option value="CLP">Peso chileno (CLP)</option>
              <option value="EUR">Euro (EUR)</option>
              <option value="ARS">Peso argentino (ARS)</option>
              <option value="PEN">Sol peruano (PEN)</option>
              <option value="MXN">Peso mexicano (MXN)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Configuración de Datos */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Database className="w-5 h-5 mr-2" />
          Gestión de Datos
        </h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Guardado automático
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Guardar automáticamente cambios en conversaciones
              </p>
            </div>
            <button
              onClick={() => handleAccountChange('autoSave', !accountSettings.autoSave)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                accountSettings.autoSave 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  accountSettings.autoSave ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Respaldo automático
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Crear respaldos automáticos de tus datos
              </p>
            </div>
            <button
              onClick={() => handleAccountChange('backupEnabled', !accountSettings.backupEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                accountSettings.backupEnabled 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  accountSettings.backupEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Retención de datos
            </label>
            <select
              value={accountSettings.dataRetention}
              onChange={(e) => handleAccountChange('dataRetention', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="3months">3 meses</option>
              <option value="6months">6 meses</option>
              <option value="1year">1 año</option>
              <option value="2years">2 años</option>
              <option value="5years">5 años</option>
              <option value="forever">Indefinido</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // ===============================================
  // 🔒 SECCIÓN PRIVACIDAD
  // ===============================================
  
  // Agregar este código en Configuration.tsx antes de renderPrivacidadSection

// ===============================================
// 🔐 MODAL CAMBIAR CONTRASEÑA
// ===============================================
const ChangePasswordModal = ({ isOpen, onClose, isDark }) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const validatePassword = (password) => {
    const minLength = 12;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return `La contraseña debe tener al menos ${minLength} caracteres`;
    }
    if (!hasUpperCase) {
      return 'La contraseña debe incluir al menos una mayúscula';
    }
    if (!hasLowerCase) {
      return 'La contraseña debe incluir al menos una minúscula';
    }
    if (!hasNumbers) {
      return 'La contraseña debe incluir al menos un número';
    }
    if (!hasSpecialChar) {
      return 'La contraseña debe incluir al menos un símbolo';
    }
    return null;
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'La contraseña actual es requerida';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else {
      const passwordError = validatePassword(passwordForm.newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Debes confirmar la nueva contraseña';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('kikai_token');
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE}/user/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Contraseña actualizada exitosamente');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        onClose();
      } else {
        setErrors({ general: data.error || 'Error al cambiar la contraseña' });
      }
    } catch (error) {
      console.error('❌ Error cambiando contraseña:', error);
      setErrors({ general: 'Error al cambiar la contraseña' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Débil', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 66, label: 'Media', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Fuerte', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Cambiar Contraseña
            </h3>
            <button 
              onClick={onClose}
              className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <X size={24} />
            </button>
          </div>

          {errors.general && (
            <div className={`mb-4 p-3 rounded ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'}`}>
              {errors.general}
            </div>
          )}

          <div className="space-y-4">
            {/* Contraseña Actual */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Contraseña actual
              </label>
              <div className="relative">
                <Lock className={`absolute left-3 top-3 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => {
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value });
                    setErrors({ ...errors, currentPassword: null });
                  }}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg ${
                    errors.currentPassword 
                      ? 'border-red-500' 
                      : isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                  }`}
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-3"
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
              )}
            </div>

            {/* Nueva Contraseña */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Nueva contraseña
              </label>
              <div className="relative">
                <Lock className={`absolute left-3 top-3 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => {
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value });
                    setErrors({ ...errors, newPassword: null });
                  }}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg ${
                    errors.newPassword 
                      ? 'border-red-500' 
                      : isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                  }`}
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-3"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
              )}
              
              {/* Barra de fortaleza */}
              {passwordForm.newPassword && (
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-500">Fortaleza:</span>
                    <span className={`text-xs font-medium ${passwordStrength.color.replace('bg-', 'text-')}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                      style={{ width: `${passwordStrength.strength}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Confirmar nueva contraseña
              </label>
              <div className="relative">
                <Lock className={`absolute left-3 top-3 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => {
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value });
                    setErrors({ ...errors, confirmPassword: null });
                  }}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg ${
                    errors.confirmPassword 
                      ? 'border-red-500' 
                      : isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300'
                  }`}
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-3"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
            >
              {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const NotificationScheduleModal = ({ isOpen, onClose, isDark }) => {
  const [schedule, setSchedule] = useState({
    enabled: true,
    startTime: '09:00',
    endTime: '22:00',
    weekdays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    },
    quietHours: {
      enabled: true,
      start: '23:00',
      end: '08:00'
    }
  });

  const handleSave = () => {
    setPreferences(prev => ({
      ...prev,
      notificationSchedule: schedule
    }));
    setHasChanges(true);
    onClose();
  };

  if (!isOpen) return null;

  const weekdayLabels = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Horarios de Notificación
            </h3>
            <button onClick={onClose} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}>
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className={isDark ? 'text-white' : 'text-gray-900'}>
                Restringir por horario
              </span>
              <button
                onClick={() => setSchedule({ ...schedule, enabled: !schedule.enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  schedule.enabled ? 'bg-blue-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  schedule.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {schedule.enabled && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Desde
                    </label>
                    <input
                      type="time"
                      value={schedule.startTime}
                      onChange={(e) => setSchedule({ ...schedule, startTime: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Hasta
                    </label>
                    <input
                      type="time"
                      value={schedule.endTime}
                      onChange={(e) => setSchedule({ ...schedule, endTime: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Días activos
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {Object.entries(weekdayLabels).map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => setSchedule({
                          ...schedule,
                          weekdays: { ...schedule.weekdays, [key]: !schedule.weekdays[key] }
                        })}
                        className={`px-2 py-2 rounded text-xs font-medium transition-colors ${
                          schedule.weekdays[key]
                            ? 'bg-blue-600 text-white'
                            : isDark
                            ? 'bg-gray-700 text-gray-400'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {label.substring(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Horario silencioso
                    </span>
                    <button
                      onClick={() => setSchedule({
                        ...schedule,
                        quietHours: { ...schedule.quietHours, enabled: !schedule.quietHours.enabled }
                      })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        schedule.quietHours.enabled ? 'bg-blue-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        schedule.quietHours.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {schedule.quietHours.enabled && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Desde
                        </label>
                        <input
                          type="time"
                          value={schedule.quietHours.start}
                          onChange={(e) => setSchedule({
                            ...schedule,
                            quietHours: { ...schedule.quietHours, start: e.target.value }
                          })}
                          className={`w-full px-2 py-1 text-sm rounded border ${
                            isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Hasta
                        </label>
                        <input
                          type="time"
                          value={schedule.quietHours.end}
                          onChange={(e) => setSchedule({
                            ...schedule,
                            quietHours: { ...schedule.quietHours, end: e.target.value }
                          })}
                          className={`w-full px-2 py-1 text-sm rounded border ${
                            isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomizeSoundsModal = ({ isOpen, onClose, isDark }) => {
  const [sounds, setSounds] = useState({
    newMessage: 'default',
    systemAlert: 'chime',
    success: 'ding',
    error: 'buzz',
    volume: 70
  });

  const soundOptions = [
    { value: 'default', label: 'Por defecto' },
    { value: 'chime', label: 'Campanilla' },
    { value: 'ding', label: 'Ding' },
    { value: 'buzz', label: 'Zumbido' },
    { value: 'ping', label: 'Ping' },
    { value: 'none', label: 'Sin sonido' }
  ];

  const handleSave = () => {
    setPreferences(prev => ({
      ...prev,
      notificationSounds: sounds
    }));
    setHasChanges(true);
    onClose();
  };

  const playSound = (soundType) => {
    console.log('🔊 Reproduciendo sonido:', soundType);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-lg w-full max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Personalizar Sonidos
            </h3>
            <button onClick={onClose} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}>
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Volumen general
                </label>
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {sounds.volume}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={sounds.volume}
                onChange={(e) => setSounds({ ...sounds, volume: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>

            <div className="space-y-3">
              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nuevo mensaje
                </label>
                <div className="flex gap-2">
                  <select
                    value={sounds.newMessage}
                    onChange={(e) => setSounds({ ...sounds, newMessage: e.target.value })}
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    {soundOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => playSound(sounds.newMessage)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Volume2 size={18} />
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Alerta del sistema
                </label>
                <div className="flex gap-2">
                  <select
                    value={sounds.systemAlert}
                    onChange={(e) => setSounds({ ...sounds, systemAlert: e.target.value })}
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    {soundOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => playSound(sounds.systemAlert)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Volume2 size={18} />
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Éxito
                </label>
                <div className="flex gap-2">
                  <select
                    value={sounds.success}
                    onChange={(e) => setSounds({ ...sounds, success: e.target.value })}
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    {soundOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => playSound(sounds.success)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Volume2 size={18} />
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Error
                </label>
                <div className="flex gap-2">
                  <select
                    value={sounds.error}
                    onChange={(e) => setSounds({ ...sounds, error: e.target.value })}
                    className={`flex-1 px-3 py-2 rounded-lg border ${
                      isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    {soundOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => playSound(sounds.error)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    <Volume2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

  const renderPrivacidadSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Configuración de Privacidad y Seguridad
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Controla tu privacidad, seguridad y acceso a la información
        </p>
      </div>

      {/* Configuración de Seguridad */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Shield className="w-5 h-5 mr-2" />
          Configuración de Seguridad
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Mostrar email en perfil público
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Otros usuarios podrán ver tu email
              </p>
            </div>
            <button
              onClick={() => handleSecurityChange('showEmail', !securitySettings.showEmail)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.showEmail 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.showEmail ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Autenticación de dos factores
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Agregar una capa extra de seguridad a tu cuenta
              </p>
            </div>
            <button
              onClick={() => handleSecurityChange('twoFactorEnabled', !securitySettings.twoFactorEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.twoFactorEnabled 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Cierre automático de sesión
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Cerrar sesión automáticamente por inactividad
              </p>
            </div>
            <button
              onClick={() => handleSecurityChange('autoLogout', !securitySettings.autoLogout)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.autoLogout 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.autoLogout ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Tiempo de sesión (minutos)
            </label>
            <select
              value={securitySettings.sessionTimeout}
              onChange={(e) => handleSecurityChange('sessionTimeout', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="15">15 minutos</option>
              <option value="30">30 minutos</option>
              <option value="60">1 hora</option>
              <option value="120">2 horas</option>
              <option value="480">8 horas</option>
              <option value="never">Nunca</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Expiración de contraseña
            </label>
            <select
              value={securitySettings.passwordExpiry}
              onChange={(e) => handleSecurityChange('passwordExpiry', e.target.value)}
              className={`w-full px-4 py-3 rounded-lg border transition-all ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:ring-2 focus:ring-blue-200`}
            >
              <option value="30">30 días</option>
              <option value="60">60 días</option>
              <option value="90">90 días</option>
              <option value="180">180 días</option>
              <option value="365">1 año</option>
              <option value="never">Nunca expira</option>
            </select>
          </div>
        </div>
      </div>

      {/* Privacidad de Datos */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Lock className="w-5 h-5 mr-2" />
          Privacidad de Datos
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Recordar mis sesiones
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Mantener sesión iniciada en este dispositivo
              </p>
            </div>
            <button
              onClick={() => handleSecurityChange('rememberMe', !securitySettings.rememberMe)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.rememberMe 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.rememberMe ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Alertas de inicio de sesión
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Recibir notificaciones de nuevos inicios de sesión
              </p>
            </div>
            <button
              onClick={() => handleSecurityChange('loginAlerts', !securitySettings.loginAlerts)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings.loginAlerts 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Gestión de Contraseña */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Key className="w-5 h-5 mr-2" />
          Gestión de Contraseña
        </h4>

       <div className="space-y-3">
         <button 
           onClick={() => setShowChangePasswordModal(true)}
           className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
         >
           <Edit className="w-4 h-4 mr-2" />
           Cambiar contraseña
         </button>
         
         <button 
           onClick={handleDownloadRecoveryKeys}
           className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
         >
           <Download className="w-4 h-4 mr-2" />
           Descargar claves de recuperación
         </button>
       </div>

        <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border`}>
          <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
            <strong>Recomendación:</strong> Usa una contraseña fuerte con al menos 12 caracteres, incluyendo mayúsculas, minúsculas, números y símbolos.
          </p>
        </div>
      </div>
    </div>
  );

  // ===============================================
  // 🔔 SECCIÓN NOTIFICACIONES
  // ===============================================

  const renderNotificacionesSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Configuración de Notificaciones
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Controla qué notificaciones quieres recibir y cómo
        </p>
      </div>

      {/* Notificaciones Generales */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Bell className="w-5 h-5 mr-2" />
          Preferencias Generales
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Notificaciones del sistema
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Recibir notificaciones importantes del sistema
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('notifications', !preferences.notifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.notifications 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.notifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Notificaciones por email
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Recibir emails sobre actualizaciones importantes
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('emailNotifications', !preferences.emailNotifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.emailNotifications 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Actualizaciones del sistema
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Notificar sobre nuevas versiones y características
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('systemUpdates', !preferences.systemUpdates)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.systemUpdates 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.systemUpdates ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Notificaciones por Dispositivo */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Smartphone className="w-5 h-5 mr-2" />
          Por Tipo de Dispositivo
        </h4>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Volume2 className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Notificaciones de sonido
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Reproducir sonidos para notificaciones
                </p>
              </div>
            </div>
            <button
              onClick={() => handlePreferenceChange('soundNotifications', !preferences.soundNotifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.soundNotifications 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.soundNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Laptop className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Notificaciones del navegador
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Mostrar notificaciones en el navegador web
                </p>
              </div>
            </div>
            <button
              onClick={() => handlePreferenceChange('browserNotifications', !preferences.browserNotifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.browserNotifications 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.browserNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Smartphone className={`w-5 h-5 mr-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Notificaciones móviles
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Recibir notificaciones en dispositivos móviles
                </p>
              </div>
            </div>
            <button
              onClick={() => handlePreferenceChange('mobileNotifications', !preferences.mobileNotifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.mobileNotifications 
                  ? 'bg-blue-600' 
                  : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.mobileNotifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Configuración Avanzada */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Settings className="w-5 h-5 mr-2" />
          Configuración Avanzada
        </h4>

       <div className="space-y-3">
         <button 
           onClick={() => setShowScheduleModal(true)}
           className="flex items-center px-4 py-2 border bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors w-full justify-center"
         >
           <Settings className="w-4 h-4 mr-2" />
           Configurar horarios de notificación
         </button>
         
         <button 
           onClick={() => setShowSoundsModal(true)}
           className="flex items-center px-4 py-2 border bg-yellow-600 text-white hover:bg-yellow-700 rounded-lg transition-colors w-full justify-center"
         >
           <Volume2 className="w-4 h-4 mr-2" />
           Personalizar sonidos
         </button>
         
         <button 
           onClick={handleDisableAllNotifications}
           className="flex items-center px-4 py-2 border bg-red-600 border-red-300 text-white hover:bg-red-700 rounded-lg transition-colors w-full justify-center"
         >
           <VolumeX className="w-4 h-4 mr-2" />
           Deshabilitar todas las notificaciones
         </button>
       </div>

        <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border`}>
          <p className={`text-sm ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
            <strong>Nota:</strong> Algunas notificaciones críticas de seguridad no se pueden desactivar.
          </p>
        </div>
      </div>
    </div>
  );

  // ===============================================
  // 🌐 SECCIÓN SISTEMA
  // ===============================================

  const renderSistemaSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Configuración del Sistema
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Personaliza la apariencia, idioma y comportamiento del sistema
        </p>
      </div>

      {/* Apariencia */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Palette className="w-5 h-5 mr-2" />
          Apariencia y Tema
        </h4>

         <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {isDark ? (
                <Moon className="w-5 h-5 mr-3 text-blue-500" />
              ) : (
                <Sun className="w-5 h-5 mr-3 text-yellow-500" />
              )}
              <div>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Tema oscuro
                </p>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Usar tema oscuro para la interfaz
                </p>
              </div>
            </div>
            <button
              
              onClick={() => {toggleTheme(); handlePreferenceChange('theme', !isDark ? 'dark' : 'light'); }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDark 
                  ? 'bg-blue-600' 
                  : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isDark ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      
      {/* Información del Sistema */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Monitor className="w-5 h-5 mr-2" />
          Información del Sistema
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Versión del sistema:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {systemInfo.version}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Estado del servidor:
              </span>
              <span className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {systemInfo.serverStatus}
                </span>
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Tiempo activo:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {systemInfo.uptime}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Consultas API hoy:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {systemInfo.apiCalls.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Almacenamiento usado:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {systemInfo.storageUsed} GB
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Último respaldo:
              </span>
              <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {systemInfo.lastBackup ? new Date(systemInfo.lastBackup).toLocaleDateString('es-CL') : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

    
      {/* Información Técnica */}
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className={`font-semibold mb-4 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <Cpu className="w-5 h-5 mr-2" />
          Información Técnica
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h5 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Navegador
            </h5>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  User Agent:
                </span>
                <span className={`text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {navigator.userAgent.split(' ')[0]}...
                </span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Resolución:
                </span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>
                  {screen.width}x{screen.height}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h5 className={`font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conexión
            </h5>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Estado:
                </span>
                <div className="flex items-center">
                  <Wifi className="w-3 h-3 mr-1 text-green-500" />
                  <span className={`text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Conectado
                  </span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  Latencia:
                </span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>
                  ~45ms
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );


  const renderSidebar = () => (
    <div className={`w-64 flex-shrink-0 ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border-r ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="p-6">
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
         {t('configuration.menu.configuration')}
        </h2>
        <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {t('configuration.menu.configurationsubtitle')}
        </p>
      </div>
      
      <nav className="px-3">
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center px-3 py-2 mb-1 rounded-lg text-left transition-all ${
                activeSection === item.id
                  ? isDark
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : isDark
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-5 h-5 mr-3" />
              {item.label}
            </button>
          );
        })}
      </nav>
    </div>
  );

  // ===============================================
  // 🖼️ RENDERIZADO PRINCIPAL
  // ===============================================

  const renderContent = () => {
    switch (activeSection) {
      case 'perfil': return renderPerfilSection();
      case 'suscripcion': return renderSuscripcionSection();
      case 'facturacion': return renderFacturacionSection();
      case 'precios-servicio': return <ServicePricingSettings />;
      case 'cuenta': return renderCuentaSection();
      case 'privacidad': return renderPrivacidadSection();
      case 'notificaciones': return renderNotificacionesSection();
      case 'sistema': return renderSistemaSection();
      default: return renderPerfilSection();
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-64 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <RefreshCw className="w-8 h-8 animate-spin mr-3" />
        {t('configuration.loader')}
      </div>
    );
  }

  return (
    <div className={`flex h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {renderSidebar()}
      
      <div className="flex-1 flex flex-col">
        {/* Header con botones de acción */}
        <div className={`flex-shrink-0 px-6 py-4 border-b ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {menuItems.find(item => item.id === activeSection)?.label || t('configuration.menu.configuration')}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Mensaje de éxito */}
              {showSuccess && (
                <div className="flex items-center px-3 py-2 bg-green-100 text-green-800 rounded-lg">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('configuration.btnresult')} 
                </div>
              )}
              
              {/* Mensaje de error */}
              {error && (
                <div className="flex items-center px-3 py-2 bg-red-100 text-red-800 rounded-lg">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {error}
                </div>
              )}
              
              {/* Botón de guardar */}
              {hasChanges && (
                <button
                  onClick={saveChanges}
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {saving ? t('configuration.save') : t('configuration.saver')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </div>
        
        {showChangePasswordModal && (
         <ChangePasswordModal
           isOpen={showChangePasswordModal}
           onClose={() => setShowChangePasswordModal(false)}
           isDark={isDark}
         />
       )}
       
       {showScheduleModal && (
         <NotificationScheduleModal
           isOpen={showScheduleModal}
           onClose={() => setShowScheduleModal(false)}
           isDark={isDark}
         />
       )}
       
       {showSoundsModal && (
         <CustomizeSoundsModal
           isOpen={showSoundsModal}
           onClose={() => setShowSoundsModal(false)}
           isDark={isDark}
         />
       )}
      </div>
    </div>
  );
};

export default Configuration;